<template>
  <div class="recent-page">
    <header class="page-header">
      <div>
        <span class="page-kicker">阅读、聆听与记录</span>
        <h1>最近</h1>
        <p>回到上次读到的地方，或从一本新书开始。</p>
      </div>
      <div class="header-actions">
        <button class="btn search-btn" type="button" @click="$router.push('/search')">
          <nricon name="search" :size="18" /><span>搜索</span>
        </button>
        <button v-if="isLoggedIn" class="btn btn--primary" type="button" @click="$router.push('/upload')">
          <nricon name="plus" :size="18" /><span>添加内容</span>
        </button>
        <button v-else class="btn btn--primary" type="button" @click="$router.push('/login')">登录</button>
      </div>
    </header>

    <section class="section-block resume-section" aria-labelledby="resume-heading">
      <div class="section-heading">
        <div><h2 id="resume-heading">继续阅读与收听</h2><p>最近打开的内容</p></div>
        <span v-if="recentReading.length" class="section-count">{{ recentReading.length }} 项</span>
      </div>

      <div v-if="loadingReading" class="continue-grid" aria-label="正在载入最近阅读" aria-busy="true">
        <div v-for="n in 2" :key="n" class="continue-skeleton"><span></span><i></i><i></i></div>
      </div>
      <div v-else-if="readingError" class="state-panel error-state">
        <nricon name="warning" :size="22" />
        <div><strong>最近阅读暂时无法载入</strong><p>请检查网络后重试。</p></div>
        <button class="btn btn--sm" type="button" @click="loadRecentReading">重试</button>
      </div>
      <div v-else-if="recentReading.length" class="continue-grid">
        <article v-for="item in recentReading" :key="item.id" class="continue-card">
      <button class="continue-cover" :class="coverClass(item)" :aria-label="`打开《${cleanTitle(item.title)}》详情`" @click="goToDetail(item.id)">
            <protected-cover v-if="item.coverImage && !brokenCovers[item.id]" :src="item.coverImage" :alt="''" :fit="item.mediaType === 'AUDIO' ? 'contain' : 'cover'" @error="hideBrokenCover(item.id)" />
            <span v-else>{{ coverInitial(item) }}</span>
          </button>
          <div class="continue-info">
            <span class="media-label"><nricon :name="mediaIcon(item)" :size="14" /> {{ mediaLabel(item) }}</span>
            <button class="continue-title" type="button" @click="goToDetail(item.id)">{{ cleanTitle(item.title) }}</button>
            <p class="continue-position">{{ progressPosition(item) }}</p>
            <div class="continue-progress" role="progressbar" :aria-valuenow="progressPercent(item)" aria-valuemin="0" aria-valuemax="100" :aria-label="`${mediaLabel(item)}进度 ${progressPercent(item)}%`">
              <span :style="{ width: progressPercent(item) + '%' }"></span>
            </div>
            <div class="continue-meta">
              <span>{{ progressPercent(item) ? progressPercent(item) + (item.mediaType === 'AUDIO' ? '% 已听' : item.mediaType === 'PDF' ? '% 已读' : '% 已读') : `正在${item.mediaType === 'AUDIO' ? '聆听' : '阅读'}` }}</span>
              <span>{{ relativeDate(item.readingProgress && item.readingProgress.updatedAt) }}</span>
            </div>
          </div>
          <button class="resume-button" type="button" @click="goToReader(item)">{{ item.mediaType === 'AUDIO' ? '继续聆听' : item.mediaType === 'PDF' ? '继续阅读' : '继续阅读' }} <nricon name="arrow-right" :size="16" /></button>
        </article>
      </div>
      <div v-else class="state-panel empty-resume">
        <span class="empty-mark"><nricon name="reading" :size="24" /></span>
        <div><strong>从一本书开始</strong><p>{{ isLoggedIn ? '添加自己的书，或到公共书库找一本喜欢的。' : '浏览公开书籍，找到下一本想读的故事。' }}</p></div>
        <div class="empty-actions">
          <router-link v-if="isLoggedIn" class="btn btn--primary btn--sm" to="/upload">添加内容</router-link>
          <router-link class="btn btn--sm" to="/discover">逛公共书库</router-link>
        </div>
      </div>
    </section>

    <section class="section-block added-section" aria-labelledby="added-heading">
      <div class="section-heading">
        <div><h2 id="added-heading">最近添加</h2><p>{{ isLoggedIn ? '来自你的上传和收藏' : '最近加入公共书库' }}</p></div>
        <router-link class="text-link" :to="isLoggedIn ? '/library' : '/discover'">查看书库 <nricon name="arrow-right" :size="15" /></router-link>
      </div>

      <div v-if="loadingAdded" class="book-grid skeleton-grid" aria-label="正在载入最近添加" aria-busy="true">
        <div v-for="n in 4" :key="n" class="book-skeleton"><span></span><i></i><i></i></div>
      </div>
      <div v-else-if="addedError" class="state-panel error-state">
        <nricon name="warning" :size="22" />
        <div><strong>最近添加暂时无法载入</strong><p>请检查网络后重试。</p></div>
        <button class="btn btn--sm" type="button" @click="loadRecentlyAdded">重试</button>
      </div>
      <div v-else-if="recentlyAdded.length" class="book-grid">
        <book-card
          v-for="book in recentlyAdded"
          :key="book.id"
          :book="book"
          :can-edit="canEdit"
          :can-remove="neverRemove"
          :go-to-detail="goToDetail"
          :handle-edit="openEditor"
          :handle-delete="deleteBook"
          :handle-remove="neverRemoveHandler"
        />
      </div>
      <div v-else class="state-panel compact-empty">
        <span>这里还没有新书。</span>
        <router-link class="text-link" :to="isLoggedIn ? '/upload' : '/discover'">{{ isLoggedIn ? '添加一本书' : '浏览公共书库' }} <nricon name="arrow-right" :size="15" /></router-link>
      </div>
    </section>

    <p v-if="readingStatsVisible" class="reading-stat"><nricon name="time" :size="16" /> <span v-if="totalReadSeconds">累计阅读 {{ formatStatsDuration(totalReadSeconds) }}</span><span v-if="totalListeningSeconds">累计聆听 {{ formatStatsDuration(totalListeningSeconds) }}</span></p>

    <book-editor
      v-if="editDialogVisible"
      :visible.sync="editDialogVisible"
      :book="currentBook || {}"
      @success="onEditSuccess"
      @close="editDialogVisible = false"
    />
  </div>
</template>

<script>
import api from '@/api'
import BookEditor from '@/components/book/BookEditor.vue'
import BookCard from '@/views/Book.vue'
import ProtectedCover from '@/components/common/ProtectedCover.vue'
import { cleanTitle } from '@/utils/title'

export default {
  name: 'RecentPage',
  components: { BookEditor, BookCard, ProtectedCover },
  data() {
    return {
      recentReading: [],
      recentlyAdded: [],
      totalReadSeconds: 0,
      totalListeningSeconds: 0,
      brokenCovers: {},
      loadingReading: false,
      loadingAdded: false,
      readingError: false,
      addedError: false,
      editDialogVisible: false,
      currentBook: null
    }
  },
  computed: {
    isLoggedIn() { return !!this.$store.state.token },
    readingStatsVisible() { return this.isLoggedIn && (this.totalReadSeconds > 0 || this.totalListeningSeconds > 0) }
  },
  created() {
    this.loadRecentReading()
    this.loadRecentlyAdded()
    if (this.isLoggedIn) this.loadReadingStats()
  },
  methods: {
    cleanTitle,
    async loadRecentReading() {
      this.loadingReading = true
      this.readingError = false
      try {
        if (!this.isLoggedIn) { this.recentReading = []; return }
        const { data } = await api.book.getRecentReading()
        this.recentReading = Array.isArray(data) ? data : []
      } catch (error) {
        this.readingError = true
      } finally {
        this.loadingReading = false
      }
    },
    async loadRecentlyAdded() {
      this.loadingAdded = true
      this.addedError = false
      try {
        const { data } = await api.book.getRecentlyAdded()
        this.recentlyAdded = Array.isArray(data) ? data : []
      } catch (error) {
        this.addedError = true
      } finally {
        this.loadingAdded = false
      }
    },
    async loadReadingStats() {
      try {
        const { data } = await api.stats.total()
        this.totalReadSeconds = Number(data && data.readingSeconds != null ? data.readingSeconds : data && data.totalSeconds) || 0
        this.totalListeningSeconds = Number(data && data.listeningSeconds) || 0
      } catch (error) { this.totalReadSeconds = 0; this.totalListeningSeconds = 0 }
    },
    formatStatsDuration(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0))
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return hours ? `${hours} 小时 ${minutes} 分钟` : (minutes ? `${minutes} 分钟` : '不到 1 分钟')
    },
    progressPercent(item) {
      const percentage = item && item.readingProgress && item.readingProgress.percentage
      return Math.max(0, Math.min(100, Math.round((Number(percentage) || 0) * 100)))
    },
    mediaLabel(item) { return item && item.mediaType === 'PDF' ? 'PDF 文档' : item && item.mediaType === 'AUDIO' ? '有声书' : '电子书' },
    mediaIcon(item) { return item && item.mediaType === 'PDF' ? 'document' : item && item.mediaType === 'AUDIO' ? 'headphones' : 'reading' },
    progressPosition(item) {
      const progress = item && item.readingProgress
      if (!progress) return '继续上次内容'
      if (item.mediaType === 'PDF' && progress.pageIndex != null) return `第 ${progress.pageIndex + 1} 页 · 共 ${progress.totalPages || item.totalPages || '—'} 页`
      if (item.mediaType === 'AUDIO') return [progress.trackTitle || '有声书', this.formatDuration(progress.positionSeconds)].filter(Boolean).join(' · ')
      return progress.chapterTitle || '继续上次阅读'
    },
    formatDuration(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0))
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const rest = seconds % 60
      return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}` : `${minutes}:${String(rest).padStart(2, '0')}`
    },
    relativeDate(value) {
      if (!value) return '最近阅读'
      const time = new Date(value).getTime()
      if (!Number.isFinite(time)) return '最近阅读'
      const days = Math.floor((Date.now() - time) / 86400000)
      if (days <= 0) return '今天'
      if (days === 1) return '昨天'
      if (days < 7) return `${days} 天前`
      return new Date(time).toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' })
    },
    coverClass(book) { return [`g-${this.coverIndex(book)}`, { 'audio-cover': book && book.mediaType === 'AUDIO' }] },
    coverIndex(book) {
      const value = cleanTitle(book && book.title) || String(book && book.id || '')
      let hash = 0
      for (let i = 0; i < value.length; i++) hash = (hash * 31 + value.charCodeAt(i)) | 0
      return Math.abs(hash) % 8
    },
    coverInitial(book) { return (cleanTitle(book && book.title) || '书').slice(0, 1) },
    hideBrokenCover(id) { this.$set(this.brokenCovers, id, true) },
    goToReader(item) {
      if (item.mediaType === 'PDF') this.$router.push({ name: 'PdfReader', params: { bookId: String(item.id) }, query: item.readingProgress && item.readingProgress.pageIndex != null ? { page: String(item.readingProgress.pageIndex + 1) } : {} })
      else if (item.mediaType === 'AUDIO') this.$router.push({ name: 'Listen', params: { bookId: String(item.id) } })
      else this.$router.push({ name: 'Reader', params: { bookId: String(item.id) } })
    },
    goToDetail(id) { this.$router.push({ name: 'BookDetail', params: { id: String(id) } }) },
    canEdit(book) { return this.isLoggedIn && book && book.userId === (this.$store.state.user && this.$store.state.user.id) },
    neverRemove() { return false },
    neverRemoveHandler() {},
    openEditor(book) { this.currentBook = { ...book }; this.editDialogVisible = true },
    async onEditSuccess() {
      this.editDialogVisible = false
      this.currentBook = null
      await this.loadRecentlyAdded()
    },
    async deleteBook(book) {
      try {
        await this.$confirm(`删除《${cleanTitle(book.title)}》会永久移除这本书、章节、笔记和阅读进度。`, '删除内容', {
          confirmButtonText: '删除内容', cancelButtonText: '取消', type: 'warning'
        })
        await api.book.deleteBook(book.id)
        this.$message.success('内容已删除')
        await Promise.all([this.loadRecentlyAdded(), this.loadRecentReading()])
      } catch (error) {
        if (error !== 'cancel') this.$message.error((error.response && error.response.data && error.response.data.message) || '删除失败，请重试')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.recent-page { padding: 38px 0 42px; }
.page-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 38px; }
.page-kicker { color: var(--nr-accent); font-size: 12px; font-weight: 700; letter-spacing: .08em; }
h1 { margin: 5px 0 4px; color: var(--nr-text); font-size: 30px; line-height: 1.25; font-weight: 650; }
.page-header p, .section-heading p { margin: 0; color: var(--nr-text-2); font-size: 14px; }
.header-actions { display: flex; align-items: center; gap: 10px; }
.search-btn { gap: 8px; }
.section-block + .section-block { margin-top: 40px; }
.section-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.section-heading h2 { margin: 0 0 4px; color: var(--nr-text); font-size: 20px; line-height: 1.4; font-weight: 650; }
.section-count { color: var(--nr-text-3); font-size: 12px; }
.text-link { display: inline-flex; min-height: 40px; align-items: center; gap: 6px; color: var(--nr-accent-text); font-size: 13px; font-weight: 600; text-decoration: none; &:hover { color: var(--nr-accent-hover); } }
.continue-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.continue-card { display: grid; min-width: 0; grid-template-columns: 70px minmax(0, 1fr); gap: 14px; align-items: center; padding: 15px; border: 1px solid var(--nr-border); border-radius: 14px; background: var(--nr-surface); }
.continue-cover { position: relative; display: grid; width: 70px; height: 98px; overflow: hidden; place-items: center; padding: 0; color: #fff; border: 0; border-radius: 5px; box-shadow: 0 4px 10px rgba(20,31,24,.13); cursor: pointer; font-family: var(--nr-font-display); font-size: 28px; font-weight: 600;
  &.g-0 { background: linear-gradient(150deg,#385c4b,#8ca48b); } &.g-1 { background: linear-gradient(150deg,#516778,#a7b5b1); } &.g-2 { background: linear-gradient(150deg,#815b46,#c29a72); } &.g-3 { background: linear-gradient(150deg,#5c596b,#a69dae); } &.g-4 { background: linear-gradient(150deg,#456b69,#a1b8aa); } &.g-5 { background: linear-gradient(150deg,#78644d,#c2ad7c); } &.g-6 { background: linear-gradient(150deg,#495b72,#8ca0b1); } &.g-7 { background: linear-gradient(150deg,#5c7354,#a6ae77); }
  img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
}
.continue-cover.audio-cover { background: var(--nr-surface-2); }
.continue-cover.audio-cover img { object-fit: contain; }
.continue-info { min-width: 0; }
.media-label { display: flex; align-items: center; gap: 5px; color: var(--nr-text-3); font-size: 11px; }
.continue-title { display: block; width: 100%; margin: 5px 0 2px; padding: 0; overflow: hidden; color: var(--nr-text); border: 0; background: none; font-size: 15px; font-weight: 650; text-align: left; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; &:hover { color: var(--nr-accent-text); } }
.continue-position { overflow: hidden; margin: 0; color: var(--nr-text-2); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.continue-progress { height: 4px; overflow: hidden; margin-top: 11px; border-radius: 9px; background: var(--nr-surface-2); span { display: block; height: 100%; border-radius: inherit; background: var(--nr-accent); } }
.continue-meta { display: flex; justify-content: space-between; gap: 8px; margin-top: 5px; color: var(--nr-text-3); font-size: 10px; }
.resume-button { display: inline-flex; min-height: 36px; grid-column: 1 / -1; align-items: center; justify-content: center; gap: 7px; color: var(--nr-accent-text); border: 1px solid var(--nr-border); border-radius: 9px; background: transparent; font-size: 12px; font-weight: 650; cursor: pointer; transition: color 160ms ease, background 160ms ease, border-color 160ms ease; &:hover { border-color: var(--nr-accent); background: var(--nr-accent-bg); } }
.book-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 22px 22px; }
.state-panel { display: flex; min-height: 112px; align-items: center; gap: 14px; padding: 20px; border: 1px solid var(--nr-border); border-radius: 14px; background: var(--nr-surface); color: var(--nr-text-2); }
.state-panel strong { display: block; color: var(--nr-text); font-size: 14px; }
.state-panel p { margin: 3px 0 0; color: var(--nr-text-2); font-size: 13px; }
.state-panel > .btn { margin-left: auto; }
.empty-resume { min-height: 136px; }
.empty-mark { display: grid; width: 46px; height: 46px; flex: 0 0 46px; place-items: center; color: var(--nr-accent); border-radius: 13px; background: var(--nr-accent-bg); }
.empty-actions { display: flex; gap: 8px; margin-left: auto; }
.compact-empty { min-height: 74px; justify-content: space-between; font-size: 13px; }
.reading-stat { display: flex; align-items: center; gap: 7px; margin: 28px 0 0; color: var(--nr-text-3); font-size: 12px; }
.continue-skeleton { display: grid; height: 174px; grid-template-columns: 70px 1fr; gap: 12px; align-content: center; padding: 15px; border: 1px solid var(--nr-border); border-radius: 14px; background: var(--nr-surface); }
.continue-skeleton span, .book-skeleton span { grid-row: span 3; border-radius: 5px; background: var(--nr-skeleton); }
.continue-skeleton i, .book-skeleton i { height: 10px; align-self: center; border-radius: 5px; background: var(--nr-skeleton); }
.skeleton-grid { pointer-events: none; }
.book-skeleton span { display: block; aspect-ratio: 5 / 7; }
.book-skeleton i { display: block; margin-top: 8px; }
.book-skeleton i:last-child { width: 62%; }

@media (max-width: 1199px) { .book-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
@media (max-width: 900px) { .continue-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .book-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 767px) {
  .recent-page { padding: 27px 0 18px; }
  .page-header { align-items: center; margin-bottom: 28px; }
  h1 { font-size: 26px; }
  .page-header p { max-width: 240px; font-size: 13px; }
  .header-actions { gap: 6px; }
  .header-actions .btn { min-width: 42px; padding-inline: 11px; }
  .header-actions .btn span { display: none; }
  .section-block + .section-block { margin-top: 30px; }
  .section-heading h2 { font-size: 18px; }
  .section-heading p { font-size: 12px; }
  .continue-grid { display: flex; overflow-x: auto; margin-right: -16px; padding: 1px 16px 8px 1px; scroll-snap-type: x mandatory; scrollbar-width: none; }
  .continue-grid::-webkit-scrollbar { display: none; }
  .continue-card { min-width: min(84vw, 340px); scroll-snap-align: start; }
  .book-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 22px 16px; }
  .state-panel { flex-wrap: wrap; padding: 16px; }
  .empty-actions { width: 100%; margin-left: 60px; }
  .reading-stat { margin-top: 24px; }
}
</style>
